import type { AdsConfig } from '../../config/config';
import type { BillingService } from '../payments/billingService';
import type { AdPresenter, AdResult, RewardedAdService } from './adService';

/**
 * Stub rewarded-ad service. Holds the *policy* (is an ad due? is the player
 * ad-free? how long / when skippable?) and delegates the actual showing to an
 * AdPresenter. No real ad network is contacted. Turning FEATURES.ads on makes
 * this present the placeholder ad overlay on the configured cadence; wiring a
 * real ad SDK later means swapping the presenter / this service, nothing else.
 */
export class StubAdService implements RewardedAdService {
  private readonly enabled: boolean;
  private readonly cfg: AdsConfig;
  private readonly billing: BillingService;
  private readonly presenter: AdPresenter;

  constructor(opts: {
    enabled: boolean;
    cfg: AdsConfig;
    billing: BillingService;
    presenter: AdPresenter;
  }) {
    this.enabled = opts.enabled;
    this.cfg = opts.cfg;
    this.billing = opts.billing;
    this.presenter = opts.presenter;
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  async maybeShowRewarded(deathCount: number): Promise<AdResult> {
    if (!this.enabled) return 'disabled';
    // Respect a purchased ad-free entitlement.
    const entitlements = await this.billing.getEntitlements();
    if (entitlements.adFree) return 'disabled';
    // Frequency: only every Nth death (config-driven). deathCount is 1-based.
    const n = Math.max(1, this.cfg.showEveryNthDeath);
    if (deathCount % n !== 0) return 'not_due';

    return this.presenter.present({
      lengthSec: this.cfg.lengthSec,
      skipAfterSec: this.cfg.skipAfterSec,
    });
  }
}
